import { useState, useEffect } from "react"
import { useApolloClient } from "@apollo/client"
import { FormikErrors, useFormik } from "formik"
import { BatchItem } from "../BatchItem"
import { updateItem } from "../reducer"
import { useBatchPageContext } from "../useBatchPageContext"
import { FormValues, ValidationErrorMessages } from "./interfaces"
import {
    checkFileName,
    validateNameUnique,
    validateFilenameExists,
    validateSourceTextLength,
    validateTargetTextLength,
    formHasValidationErrors,
    getErrorMsgs,
    isDuplicateName
} from "./ValidationHelpers"

export function useBatchItemValidator(item: BatchItem) {
    const { state, dispatch } = useBatchPageContext()
    const client = useApolloClient()
    const [initialErrors, setInitialErrors] = useState(state.initialErrors[item.index])

    async function validate(values: FormValues): Promise<FormikErrors<FormValues>> {
        let errors: FormikErrors<FormValues> = { ...state.serverOperationErrors[item.index] }

        await checkFileName(values, errors, client)
        validateNameUnique(values, errors, item, state.batchItems)
        validateFilenameExists(values, errors)
        await validateSourceTextLength(values, errors)
        await validateTargetTextLength(values, errors)

        dispatch(
            updateItem(item.index, {
                ...values,
                valid: !formHasValidationErrors(errors),
                errorStringForCSV: getErrorMsgs(errors)
            })
        )
        return errors
    }

    const formik = useFormik<FormValues>({
        initialValues: {
            name: item.name,
            source: item.source,
            target: item.target
        },
        initialErrors: {
            ...initialErrors,
            ...state.serverOperationErrors[item.index]
        },
        onSubmit: (values: FormValues) => {},
        validate: validate,
        validateOnMount: false,
        validateOnBlur: true,
        validateOnChange: false,
        enableReinitialize: true,
        onReset: () => setInitialErrors({})
    })

    //when another batch item is validated,
    // & state is updated, we need to know
    // check that this row isn't a duplicate
    useEffect(() => {
        const duplicate = isDuplicateName(item.name, item, state.batchItems)
        if (duplicate && formik.errors.name !== ValidationErrorMessages.DUPLICATE_FILE_NAME) {
            const updatedErrors = {
                ...formik.errors,
                name: ValidationErrorMessages.DUPLICATE_FILE_NAME
            }
            formik.setErrors(updatedErrors)
            dispatch(
                updateItem(item.index, {
                    valid: !formHasValidationErrors(updatedErrors),
                    errorStringForCSV: getErrorMsgs(updatedErrors)
                })
            )
        }
        if (!duplicate && formik.errors.name === ValidationErrorMessages.DUPLICATE_FILE_NAME) {
            const updatedErrors = { ...formik.errors, name: undefined }
            formik.setErrors(updatedErrors)

            dispatch(
                updateItem(item.index, {
                    valid: !formHasValidationErrors(updatedErrors),
                    errorStringForCSV: getErrorMsgs(updatedErrors)
                })
            )
        }
    }, [dispatch, formik, item, state.batchItems])

    useEffect(() => {
        // attach server operation errors
        formik.setErrors({ ...formik.errors, ...state.serverOperationErrors[item.index] })
    }, [state.serverOperationErrors])

    return formik
}
